import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { Anchor, Box, Button, Flex, Progress, Text } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useQueryGetUserLogged } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { Icon } from '@flecto/ui'
import { sleep } from '@flecto/utils'

import { useOnboardingActions } from '../../hooks/useOnboardingActions'
import { useOnboardingNavigation } from '../../hooks/useOnboardingNavigation'
import { useFormContext } from './FormOnboarding'
import { StepWrapper } from './StepWrapper'
import classes from './styles.module.css'

export function StepSubmit() {
  const form = useFormContext()
  const track = useAnalytics('segment')
  const { t } = useTranslation('saas', { keyPrefix: 'onboarding' })
  const { data: userLogged } = useQueryGetUserLogged()
  const { moveToStep, currentStep, totalSteps } = useOnboardingNavigation()
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)

  const ref = useRef<HTMLDivElement>(null)

  const saveAllDataOnboarding = useOnboardingActions()

  const submitOnboarding = async () => {
    // Create address, update business info
    try {
      setHasError(false)
      setIsLoading(true)

      await updateProgress(t('steps.submit.gathering_info'), 20, 2000)

      await updateProgress(t('steps.submit.setting_main_location'), 40, 2000)

      await updateProgress(t('steps.submit.updating_business_info'), 60, 2000)

      await saveAllDataOnboarding()
    } catch (error) {
      setHasError(true)

      track('ONBOARDING_SUBMIT_ERROR', {
        error: (error as Error).message ?? 'Uncaught error',
      })

      return
    }

    // Update user onboarded_at
    try {
      await updateProgress(t('steps.submit.updating_account'), 80, 2000)

      await updateProgress(t('steps.submit.ready_to_go'), 100, 0)

      localStorage.removeItem('form_onboarding')

      const user = form.getValues().step_basic_info
      const address = form.getValues().step_location
      const qualification = form.getValues().step_business_info

      track('ONBOARDING_SUBMIT_COMPLETED', {
        user_id: String(userLogged?.id),
        company_name: user.company_name,
        qty_employees: user.qty_employees,
        avg_bookings_per_month: qualification.avg_bookings_per_month,
        flecto_products_interested_on:
          qualification.flecto_products_interested_on,
        systems_to_replace: qualification.systems_to_replace,
        business_category: qualification.business_category,
        country_of_operations: address.address.address_country_code ?? '',
      })
    } catch (error) {
      setHasError(true)

      track('ONBOARDING_SUBMIT_ERROR', {
        error: `Error updating onboarding_at on user: ${(error as Error).message}`,
      })
    } finally {
      setIsLoading(false)
    }

    return
  }

  const updateProgress = async (
    title: string,
    percentage: number,
    delay: number
  ) => {
    setProgress(percentage)

    const boxTitles = ref.current

    if (boxTitles) {
      const titleDom = document.createElement('div')
      titleDom.classList.add(classes.progressTitle, classes.slideTop)
      titleDom.innerHTML = title
      boxTitles.appendChild(titleDom)
    }

    if (delay) {
      await sleep(delay)
    }
  }

  useEffect(() => {
    if (currentStep > totalSteps) {
      submitOnboarding()
    }
  }, [currentStep, totalSteps])

  return (
    <StepWrapper stepNumber={5} withActions={false} withStepsCount={false}>
      <Box my='xl' className={classes.boxProgress}>
        <Box ref={ref} className={classes.boxProgressTitles} />
        <Progress
          value={progress}
          mt='md'
          h={10}
          transitionDuration={200}
          color={hasError ? 'red' : 'brand'}
        />
      </Box>
      <Flex justify='space-between'>
        <Flex align='center' gap='xs'>
          {hasError && (
            <>
              <Text c='red'>{t('steps.submit.error')}</Text>
              <Anchor onClick={() => moveToStep(1)}>
                {t('steps.submit.please_try_again')}
              </Anchor>
            </>
          )}
        </Flex>
        <Button
          size='lg'
          rightSection={<Icon icon={faArrowRight} />}
          loading={isLoading}
          disabled={hasError}
          color={isLoading ? 'gray.2' : 'brand'}
          onClick={() => {
            window.location.href = '/schedule'
          }}
        >
          {t('steps.submit.start_using_flecto')}
        </Button>
      </Flex>
    </StepWrapper>
  )
}
